/**
 * Admin Settings Page
 *
 * DevOps Admin only - manage admin users, contractors, and system configuration.
 */

import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthProvider';
import { useAdminConfig } from '../contexts/AdminConfigProvider';
import {
  // Custom Clients
  CustomClient,
  getCustomClients,
  createCustomClient,
  updateCustomClient,
  deleteCustomClient,
  createClientInAutotask,
  // Company Admin (Billing Controls)
  CompanyAdmin,
  getCompaniesAdmin,
  toggleBillingBlock,
  // Registered Devices
  getAllRegisteredDevices,
  // Notification diagnostics
  getNotificationDiagnostics,
  NotificationDiagnosticRow,
} from '../services/api';
import { DeviceRegistration } from '../services/types';

export default function AdminSettings() {
  const { user } = useAuth();
  const { adminEmails, contractors, isLoading, error, addAdmin, removeAdmin, refresh } = useAdminConfig();

  // Add admin form state
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Remove admin state
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  // Notification diagnostics state
  const [notifDiagRows, setNotifDiagRows] = useState<NotificationDiagnosticRow[]>([]);
  const [notifDiagLoading, setNotifDiagLoading] = useState(false);
  const [notifDiagError, setNotifDiagError] = useState<string | null>(null);

  const loadNotifDiagnostics = useCallback(async () => {
    setNotifDiagLoading(true);
    setNotifDiagError(null);
    try {
      const res = await getNotificationDiagnostics();
      setNotifDiagRows(res.resources);
    } catch (err) {
      setNotifDiagError(err instanceof Error ? err.message : 'Failed to load diagnostics');
    } finally {
      setNotifDiagLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifDiagnostics();
  }, [loadNotifDiagnostics]);

  const formatLocationAge = (iso: string | null): { label: string; tone: 'green' | 'amber' | 'red' | 'slate' } => {
    if (!iso) return { label: 'Never', tone: 'slate' };
    const minutesAgo = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (minutesAgo < 0) return { label: 'Just now', tone: 'green' };
    if (minutesAgo < 10) return { label: `${minutesAgo}m ago`, tone: 'green' };
    if (minutesAgo < 120) return { label: `${minutesAgo}m ago`, tone: 'amber' };
    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo < 24) return { label: `${hoursAgo}h ago`, tone: 'red' };
    const daysAgo = Math.floor(hoursAgo / 24);
    return { label: `${daysAgo}d ago`, tone: 'slate' };
  };

  const formatHour = (h: number) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);

  // Custom Clients state
  const [customClients, setCustomClients] = useState<CustomClient[]>([]);
  const [customClientsLoading, setCustomClientsLoading] = useState(true);
  const [customClientsError, setCustomClientsError] = useState<string | null>(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [editingClient, setEditingClient] = useState<CustomClient | null>(null);
  const [newClientName, setNewClientName] = useState('');
  const [newClientContact, setNewClientContact] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientNotes, setNewClientNotes] = useState('');
  const [savingClient, setSavingClient] = useState(false);
  const [creatingInAutotask, setCreatingInAutotask] = useState<string | null>(null);

  // Company billing management state
  const [allCompanies, setAllCompanies] = useState<CompanyAdmin[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [companySearch, setCompanySearch] = useState('');
  const [showBlockedOnly, setShowBlockedOnly] = useState(false);
  const [togglingBlock, setTogglingBlock] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Registered Devices state
  const [registeredDevices, setRegisteredDevices] = useState<DeviceRegistration[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [deviceSearch, setDeviceSearch] = useState('');

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newAdminEmail.trim().toLowerCase();

    if (!email) {
      setAddError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setAddError('Please enter a valid email address');
      return;
    }

    setIsAdding(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      await addAdmin(email);
      setNewAdminEmail('');
      setAddSuccess(`Added ${email} as admin`);
      setTimeout(() => setAddSuccess(null), 3000);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add admin');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveAdmin = async (email: string) => {
    if (!confirm(`Remove ${email} from admin list?\n\nThey will lose access to:\n- Reports tab\n- Calendar tech selector\n- Admin settings menu items`)) {
      return;
    }

    setRemovingEmail(email);
    setAddError(null);

    try {
      await removeAdmin(email);
      setAddSuccess(`Removed ${email} from admin list`);
      setTimeout(() => setAddSuccess(null), 3000);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to remove admin');
    } finally {
      setRemovingEmail(null);
    }
  };

  // Load custom clients on mount
  useEffect(() => {
    async function loadCustomClients() {
      try {
        const clients = await getCustomClients();
        setCustomClients(clients);
      } catch (error) {
        console.error('Failed to load custom clients:', error);
        setCustomClientsError(error instanceof Error ? error.message : 'Failed to load custom clients');
      } finally {
        setCustomClientsLoading(false);
      }
    }
    loadCustomClients();
  }, []);

  // Custom client handlers
  const resetClientForm = () => {
    setNewClientName('');
    setNewClientContact('');
    setNewClientPhone('');
    setNewClientNotes('');
    setEditingClient(null);
    setShowAddClient(false);
  };

  const handleSaveClient = async () => {
    if (!newClientName.trim()) return;

    setSavingClient(true);
    setCustomClientsError(null);

    try {
      if (editingClient) {
        const updated = await updateCustomClient(editingClient.id, {
          companyName: newClientName.trim(),
          contactName: newClientContact.trim() || undefined,
          phone: newClientPhone.trim() || undefined,
          notes: newClientNotes.trim() || undefined,
        });
        setCustomClients(prev => prev.map(c => c.id === updated.id ? updated : c));
      } else {
        const created = await createCustomClient({
          companyName: newClientName.trim(),
          contactName: newClientContact.trim() || undefined,
          phone: newClientPhone.trim() || undefined,
          notes: newClientNotes.trim() || undefined,
        });
        setCustomClients(prev => [...prev, created].sort((a, b) => a.companyName.localeCompare(b.companyName)));
      }
      resetClientForm();
    } catch (error) {
      setCustomClientsError(error instanceof Error ? error.message : 'Failed to save client');
    } finally {
      setSavingClient(false);
    }
  };

  const handleEditClient = (client: CustomClient) => {
    setEditingClient(client);
    setNewClientName(client.companyName);
    setNewClientContact(client.contactName || '');
    setNewClientPhone(client.phone || '');
    setNewClientNotes(client.notes || '');
    setShowAddClient(true);
  };

  const handleDeleteClient = async (clientId: string) => {
    if (!confirm('Are you sure you want to delete this client?')) return;

    try {
      await deleteCustomClient(clientId);
      setCustomClients(prev => prev.filter(c => c.id !== clientId));
    } catch (error) {
      setCustomClientsError(error instanceof Error ? error.message : 'Failed to delete client');
    }
  };

  const handleCreateInAutotask = async (client: CustomClient) => {
    if (!confirm(`Create "${client.companyName}" in AutoTask? This will sync the company to AutoTask.`)) return;

    setCreatingInAutotask(client.id);
    setCustomClientsError(null);

    try {
      const result = await createClientInAutotask(client.id);
      setCustomClients(prev => prev.map(c =>
        c.id === client.id
          ? { ...c, autoTaskId: result.autotaskId, source: 'autotask' as const }
          : c
      ));
    } catch (error) {
      setCustomClientsError(error instanceof Error ? error.message : 'Failed to create in AutoTask');
    } finally {
      setCreatingInAutotask(null);
    }
  };

  // Load all companies for billing management
  useEffect(() => {
    async function loadCompanies() {
      try {
        const companies = await getCompaniesAdmin({
          search: companySearch || undefined,
          blockedOnly: showBlockedOnly,
        });
        setAllCompanies(companies);
      } catch (error) {
        console.error('Failed to load companies:', error);
        setCompaniesError(error instanceof Error ? error.message : 'Failed to load companies');
      } finally {
        setCompaniesLoading(false);
      }
    }
    loadCompanies();
  }, [companySearch, showBlockedOnly]);

  // Debounced search for companies
  useEffect(() => {
    const timer = setTimeout(() => {
      setCompanySearch(debouncedSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  // Load registered devices on mount
  useEffect(() => {
    async function loadDevices() {
      try {
        const devices = await getAllRegisteredDevices();
        setRegisteredDevices(devices);
      } catch (error) {
        console.error('Failed to load registered devices:', error);
        setDevicesError(error instanceof Error ? error.message : 'Failed to load devices');
      } finally {
        setDevicesLoading(false);
      }
    }
    loadDevices();
  }, []);

  // Filter devices by search
  const filteredDevices = deviceSearch
    ? registeredDevices.filter(d => {
        const search = deviceSearch.toLowerCase();
        return (
          d.hostname?.toLowerCase().includes(search) ||
          d.companyName?.toLowerCase().includes(search) ||
          String(d.deviceId).includes(search) ||
          d.systemInfo?.operatingSystem?.toLowerCase().includes(search) ||
          d.screenConnectGuid?.toLowerCase().includes(search)
        );
      })
    : registeredDevices;

  // Handle billing block toggle
  const handleToggleBillingBlock = async (company: CompanyAdmin) => {
    const newBlocked = !company.billingBlocked;
    const reason = newBlocked ? blockReason.trim() || undefined : undefined;

    if (newBlocked && !confirm(`Block billing for "${company.companyName}"? Techs will not be able to log time to this company.`)) {
      return;
    }

    setTogglingBlock(company.id);
    setCompaniesError(null);

    try {
      const updated = await toggleBillingBlock(company.autoTaskId, newBlocked, reason);
      setAllCompanies(prev => prev.map(c =>
        c.id === company.id ? { ...c, ...updated } : c
      ));
      setBlockReason('');
    } catch (error) {
      setCompaniesError(error instanceof Error ? error.message : 'Failed to toggle billing block');
    } finally {
      setTogglingBlock(null);
    }
  };

  return (
    <>
      <PageHeader userEmail={user?.email} />

      <div className="max-w-4xl mx-auto py-6 px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Settings</h1>
            <p className="text-slate-400 mt-1">Manage who has admin access to AutoTask Hub.</p>
          </div>
          <button
            onClick={() => refresh()}
            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Admin Users */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-2">Admin Users</h2>
              <p className="text-sm text-slate-400 mb-4">
                Admins can view Reports, see all techs in Calendar, and access admin settings.
              </p>

              {/* Success/Error messages */}
              {addSuccess && (
                <div className="bg-green-500/20 border border-green-500/30 text-green-400 rounded px-3 py-2 mb-4 text-sm">
                  {addSuccess}
                </div>
              )}
              {addError && (
                <div className="bg-red-500/20 border border-red-500/30 text-red-400 rounded px-3 py-2 mb-4 text-sm">
                  {addError}
                </div>
              )}

              {/* Add admin form */}
              <form onSubmit={handleAddAdmin} className="flex gap-2 mb-4">
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="Enter email address..."
                  className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={isAdding || !newAdminEmail.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded font-medium transition-colors"
                >
                  {isAdding ? 'Adding...' : 'Add Admin'}
                </button>
              </form>

              {/* Admin list */}
              <div className="space-y-2">
                {adminEmails.map((email) => (
                  <div key={email} className="flex items-center justify-between bg-slate-700/50 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-white">{email}</span>
                      {email.toLowerCase() === user?.email?.toLowerCase() && (
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">You</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveAdmin(email)}
                      disabled={removingEmail === email || adminEmails.length <= 1}
                      className="text-red-400 hover:text-red-300 disabled:text-slate-500 disabled:cursor-not-allowed p-1 transition-colors"
                      title={adminEmails.length <= 1 ? "Cannot remove the last admin" : `Remove ${email}`}
                    >
                      {removingEmail === email ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Contractors */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-2">Contractors</h2>
              <p className="text-sm text-slate-400 mb-4">
                Contractors visible in the Calendar tech selector for admins.
              </p>
              <div className="space-y-2">
                {contractors.map((c, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-700/50 rounded px-3 py-2">
                    <div>
                      <span className="text-white font-medium">{c.name}</span>
                      <span className="text-slate-400 ml-2">({c.email})</span>
                    </div>
                    <span className="text-xs text-slate-500">TimeTech ID: {c.timetechUserId}</span>
                  </div>
                ))}
                {contractors.length === 0 && (
                  <div className="text-slate-500 text-sm py-2">No contractors configured</div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-4">
                To modify contractors: Update the <code className="bg-slate-700 px-1 rounded">contractors</code> setting in Cosmos DB
              </p>
            </div>

            {/* Custom Clients */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-lg font-semibold text-white">Custom Clients</h2>
                  <p className="text-sm text-slate-400">
                    Manage clients not synced from AutoTask (for time billing to non-AT companies)
                  </p>
                </div>
                {!showAddClient && (
                  <button
                    onClick={() => setShowAddClient(true)}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    + Add Client
                  </button>
                )}
              </div>

              {/* Add/Edit Form */}
              {showAddClient && (
                <div className="mb-4 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <h3 className="text-sm font-medium text-white mb-3">
                    {editingClient ? 'Edit Client' : 'Add New Client'}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Company Name *</label>
                      <input
                        type="text"
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                        placeholder="Company name"
                        className="w-full bg-slate-900 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Contact Name</label>
                      <input
                        type="text"
                        value={newClientContact}
                        onChange={(e) => setNewClientContact(e.target.value)}
                        placeholder="Primary contact"
                        className="w-full bg-slate-900 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Phone</label>
                      <input
                        type="text"
                        value={newClientPhone}
                        onChange={(e) => setNewClientPhone(e.target.value)}
                        placeholder="Phone number"
                        className="w-full bg-slate-900 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Notes</label>
                      <input
                        type="text"
                        value={newClientNotes}
                        onChange={(e) => setNewClientNotes(e.target.value)}
                        placeholder="Internal notes"
                        className="w-full bg-slate-900 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={resetClientForm}
                      className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-white text-sm font-medium rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveClient}
                      disabled={savingClient || !newClientName.trim()}
                      className={`px-3 py-1.5 text-white text-sm font-medium rounded-md transition-colors ${
                        savingClient || !newClientName.trim()
                          ? 'bg-slate-600 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {savingClient ? 'Saving...' : editingClient ? 'Update' : 'Create'}
                    </button>
                  </div>
                </div>
              )}

              {/* Error display */}
              {customClientsError && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-md text-sm text-red-400">
                  {customClientsError}
                  <button onClick={() => setCustomClientsError(null)} className="ml-2 text-red-300 hover:text-white">×</button>
                </div>
              )}

              {/* Client List */}
              {customClientsLoading ? (
                <div className="text-center py-4 text-slate-500 text-sm">Loading custom clients...</div>
              ) : customClients.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  No custom clients yet. Add a client for non-AutoTask billing.
                </div>
              ) : (
                <div className="space-y-2">
                  {customClients.map((client) => (
                    <div key={client.id} className="flex items-center justify-between bg-slate-700/50 rounded px-3 py-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{client.companyName}</span>
                          {client.source === 'manual' && (
                            <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">Custom</span>
                          )}
                          {client.source === 'autotask' && client.autoTaskId > 0 && (
                            <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">AT #{client.autoTaskId}</span>
                          )}
                        </div>
                        {(client.contactName || client.phone) && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {client.contactName}{client.contactName && client.phone && ' • '}{client.phone}
                          </div>
                        )}
                        {client.notes && <div className="text-xs text-slate-500 mt-0.5">{client.notes}</div>}
                      </div>
                      <div className="flex gap-2">
                        {client.source === 'manual' && (
                          <>
                            <button
                              onClick={() => handleEditClient(client)}
                              className="px-2 py-1 bg-slate-600 hover:bg-slate-500 text-white text-xs rounded transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleCreateInAutotask(client)}
                              disabled={creatingInAutotask === client.id}
                              className={`px-2 py-1 text-white text-xs rounded transition-colors ${
                                creatingInAutotask === client.id ? 'bg-slate-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                              }`}
                            >
                              {creatingInAutotask === client.id ? 'Creating...' : 'Create in AT'}
                            </button>
                            <button
                              onClick={() => handleDeleteClient(client.id)}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {client.source === 'autotask' && (
                          <span className="text-xs text-slate-500">Synced (read-only)</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Billing Controls */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-2">Billing Controls</h2>
              <p className="text-sm text-slate-400 mb-4">
                Block billing for former clients or companies that should not receive time entries
              </p>

              {/* Search and filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <input
                  type="text"
                  value={debouncedSearch}
                  onChange={(e) => setDebouncedSearch(e.target.value)}
                  placeholder="Search companies..."
                  className="flex-1 min-w-[200px] bg-slate-900 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                />
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showBlockedOnly}
                    onChange={(e) => setShowBlockedOnly(e.target.checked)}
                    className="rounded border-slate-600 bg-slate-700 text-red-600 focus:ring-red-500"
                  />
                  Show blocked only
                </label>
              </div>

              {/* Error display */}
              {companiesError && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-md text-sm text-red-400">
                  {companiesError}
                  <button onClick={() => setCompaniesError(null)} className="ml-2 text-red-300 hover:text-white">×</button>
                </div>
              )}

              {/* Company List */}
              {companiesLoading ? (
                <div className="text-center py-4 text-slate-500 text-sm">Loading companies...</div>
              ) : allCompanies.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  {companySearch ? 'No companies match your search' : 'No companies found'}
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {allCompanies.map((company) => (
                    <div key={company.id} className="flex items-center justify-between bg-slate-700/50 rounded px-3 py-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-white">{company.companyName}</span>
                          {company.autoTaskId > 0 && (
                            <span className="text-xs text-slate-500">#{company.autoTaskId}</span>
                          )}
                          {company.source === 'manual' && (
                            <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">Custom</span>
                          )}
                        </div>
                        {company.billingBlocked && (
                          <div className="text-xs text-red-400 mt-1">
                            Blocked by {company.billingBlockedBy?.split('@')[0] || 'admin'}
                            {company.billingBlockedAt && <> on {new Date(company.billingBlockedAt).toLocaleDateString()}</>}
                            {company.billingBlockedReason && <> — "{company.billingBlockedReason}"</>}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleToggleBillingBlock(company)}
                        disabled={togglingBlock === company.id}
                        className={`px-3 py-1 text-white text-xs rounded font-medium transition-colors ${
                          togglingBlock === company.id
                            ? 'bg-slate-600 cursor-not-allowed'
                            : company.billingBlocked
                              ? 'bg-green-600 hover:bg-green-700'
                              : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        {togglingBlock === company.id
                          ? 'Updating...'
                          : company.billingBlocked
                            ? 'Unblock'
                            : 'Block'
                        }
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Block reason input */}
              {allCompanies.length > 0 && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Optional: Reason for blocking (e.g., 'Former client as of Jan 2025')"
                    className="w-full bg-slate-900 border border-slate-600 text-white text-xs rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              )}

              <p className="text-xs text-slate-500 mt-3">
                Blocked companies will show a warning when techs try to log time. Showing {allCompanies.length} companies.
              </p>
            </div>

            {/* Registered Devices */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-white">Registered Devices</h2>
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                    {registeredDevices.length}
                  </span>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                All devices registered via Datto RMM font detection across all companies
              </p>

              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  placeholder="Search by hostname, company, OS, or device ID..."
                  className="w-full bg-slate-900 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Error display */}
              {devicesError && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-md text-sm text-red-400">
                  {devicesError}
                  <button onClick={() => setDevicesError(null)} className="ml-2 text-red-300 hover:text-white">x</button>
                </div>
              )}

              {/* Devices Table */}
              {devicesLoading ? (
                <div className="text-center py-4 text-slate-500 text-sm">Loading devices...</div>
              ) : filteredDevices.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  {deviceSearch ? 'No devices match your search' : 'No registered devices found'}
                </div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-800">
                      <tr className="text-left text-slate-400 border-b border-slate-700">
                        <th className="pb-2 pr-3 font-medium">Device ID</th>
                        <th className="pb-2 pr-3 font-medium">Hostname</th>
                        <th className="pb-2 pr-3 font-medium">Company</th>
                        <th className="pb-2 pr-3 font-medium">OS</th>
                        <th className="pb-2 pr-3 font-medium">Last Seen</th>
                        <th className="pb-2 pr-3 font-medium">SC GUID</th>
                        <th className="pb-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {filteredDevices.map((device) => (
                        <tr key={device.id} className="hover:bg-slate-700/30">
                          <td className="py-2 pr-3 text-white font-mono">{device.deviceId}</td>
                          <td className="py-2 pr-3 text-white">{device.hostname}</td>
                          <td className="py-2 pr-3 text-slate-300">{device.companyName || '-'}</td>
                          <td className="py-2 pr-3 text-slate-300 max-w-[150px] truncate" title={device.systemInfo?.operatingSystem}>
                            {device.systemInfo?.operatingSystem || '-'}
                          </td>
                          <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">
                            {device.lastSeenAt
                              ? new Date(device.lastSeenAt).toLocaleDateString() + ' ' + new Date(device.lastSeenAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : '-'}
                          </td>
                          <td className="py-2 pr-3 text-slate-500 font-mono text-xs max-w-[100px] truncate" title={device.screenConnectGuid}>
                            {device.screenConnectGuid ? device.screenConnectGuid.substring(0, 8) + '...' : '-'}
                          </td>
                          <td className="py-2">
                            <span className={`px-1.5 py-0.5 text-xs rounded ${
                              device.status === 'active'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-slate-500/20 text-slate-400'
                            }`}>
                              {device.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {filteredDevices.length > 0 && (
                <p className="text-xs text-slate-500 mt-3">
                  Showing {filteredDevices.length} of {registeredDevices.length} devices
                </p>
              )}
            </div>

            {/* Notification Diagnostics */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-white">Push & Location Diagnostics</h2>
                  <p className="text-xs text-slate-400 mt-1">
                    One row per active tech. Shows whether they've subscribed for push, when they last reported a location,
                    and what their per-event toggles + work hours look like. Use this to spot misconfigured techs.
                  </p>
                </div>
                <button
                  onClick={loadNotifDiagnostics}
                  disabled={notifDiagLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50"
                >
                  {notifDiagLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>

              {notifDiagError && (
                <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
                  {notifDiagError}
                </div>
              )}

              {notifDiagLoading && notifDiagRows.length === 0 ? (
                <div className="text-sm text-slate-400 py-4">Loading…</div>
              ) : notifDiagRows.length === 0 ? (
                <div className="text-sm text-slate-400 py-4">No active resources found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-700">
                        <th className="py-2 pr-3 font-medium">Tech</th>
                        <th className="py-2 px-2 font-medium text-center">Push</th>
                        <th className="py-2 px-2 font-medium text-center">Last Location</th>
                        <th className="py-2 px-2 font-medium text-center">Loc Consent</th>
                        <th className="py-2 px-2 font-medium text-center">New</th>
                        <th className="py-2 px-2 font-medium text-center">Assigned</th>
                        <th className="py-2 px-2 font-medium text-center">Replies</th>
                        <th className="py-2 pl-2 font-medium">Work Hours (PT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notifDiagRows.map(row => {
                        const locAge = formatLocationAge(row.lastLocationAt);
                        const ageColor = locAge.tone === 'green' ? 'text-green-400'
                          : locAge.tone === 'amber' ? 'text-amber-400'
                          : locAge.tone === 'red' ? 'text-red-400'
                          : 'text-slate-500';
                        return (
                          <tr key={row.resourceId} className="border-b border-slate-700/50 hover:bg-slate-900/40">
                            <td className="py-2 pr-3">
                              <div className="text-slate-200">{row.fullName || '(no name)'}</div>
                              <div className="text-[10px] text-slate-500">{row.email}</div>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className={row.pushSubscriptionCount > 0 ? 'text-green-400 font-medium' : 'text-slate-500'}>
                                {row.pushSubscriptionCount}
                              </span>
                            </td>
                            <td className={`py-2 px-2 text-center ${ageColor}`}>
                              {locAge.label}
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className={row.locationConsent ? 'text-green-400' : 'text-slate-500'}>
                                {row.locationConsent ? 'On' : 'Off'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className={row.notifyNewTickets ? 'text-green-400' : 'text-slate-500'}>
                                {row.notifyNewTickets ? '✓' : '·'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className={row.notifyAssignedTickets ? 'text-green-400' : 'text-slate-500'}>
                                {row.notifyAssignedTickets ? '✓' : '·'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className={row.notifyCustomerReplies ? 'text-green-400' : 'text-slate-500'}>
                                {row.notifyCustomerReplies ? '✓' : '·'}
                              </span>
                            </td>
                            <td className="py-2 pl-2 text-slate-400">
                              {formatHour(row.workHoursStart)} – {formatHour(row.workHoursEnd)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="text-[10px] text-slate-500 mt-2">
                    <strong>Push</strong> = browser subscriptions registered for this tech (0 = they haven't clicked Subscribe in User Settings).
                    {' '}<strong>Last Location</strong> turns red after 2h (the server marks them offline on the Field Map).
                    {' '}<strong>Loc Consent</strong> reflects the toggle in User Settings.
                  </p>
                </div>
              )}
            </div>

            {/* Debug Info */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Debug Info</h2>
              <div className="bg-slate-900 rounded p-4 font-mono text-sm space-y-1">
                <div className="text-slate-400">Current User: <span className="text-white">{user?.email || 'Unknown'}</span></div>
                <div className="text-slate-400">User ID: <span className="text-white">{user?.userId || 'Unknown'}</span></div>
                <div className="text-slate-400">Is Admin: <span className={adminEmails.some(a => a.toLowerCase() === user?.email?.toLowerCase()) ? 'text-green-400' : 'text-red-400'}>{adminEmails.some(a => a.toLowerCase() === user?.email?.toLowerCase()) ? 'Yes' : 'No'}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
